import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { globalActions, TGlobalKey } from '@redux/slices/states/global/globalSlice';
import type { RootState, AppDispatch } from '@redux/store';

type Id = string;

/**
 * Generic configuration object used to create an ID-based fetch hook.
 *
 * @typeParam TModel - The plain data model returned by the backend (e.g., `IElementType`).
 * @typeParam TClass - The domain class that the hook will expose (e.g., `CElementType`).
 */
export interface UseFetchByIdConfig<TModel extends { id: string }, TClass> {
    /**
     * Redux selector that retrieves the cached list from the store.
     * For example: `state.global.elementsTypesList`.
     */
    selector: (state: RootState) => TModel[];

    /**
     * Key of the Redux slice where the list is stored.
     * Must be a valid `TGlobalKey`.
     */
    key: TGlobalKey;

    /**
     * Function responsible for fetching one or multiple models from the backend by ID.
     * Receives the Redux `dispatch` and an array of IDs to resolve.
     */
    fetchFn: (dispatch: AppDispatch, ids: string[]) => Promise<TModel | TModel[] | null>;

    /**
     * Maps the plain backend model (`TModel`) into the corresponding domain class (`TClass`).
     * Example: `(model) => new CElementType(model)`.
     *
     * Note: this will also be called with minimal fallback models (e.g. `{ id }`)
     * when the backend does not return data for a given ID. Your class (e.g. `CEntity`)
     * can handle missing fields and default to "Unknown" as needed.
     */
    mapToClass: (model: TModel) => TClass;

    /**
     * Optional number of fetch retries before giving up and
     * storing a fallback model for unresolved IDs.
     * Defaults to 1 (no extra retries).
     */
    maxRetries?: number;
}

/**
 * Factory that creates a hook to fetch one or multiple entities by their ID.
 *
 * The returned hook behaves as:
 * - `useFetchById(id: string): TClass | null`
 * - `useFetchById(ids: string[]): TClass[] | null`
 *
 * If the backend does not return data for a given ID after `maxRetries`,
 * a minimal fallback model `{ id }` is stored in Redux and mapped to `TClass`.
 */
export function createUseFetchByIdHook<
    TModel extends { id: string },
    TClass
>(config: UseFetchByIdConfig<TModel, TClass>) {
    // Shared queue for all instances of this particular hook (per entity type).
    const idQueueRef: Set<Id> = new Set();

    function useFetchById(id: Id): TClass | null;
    function useFetchById(id: Id[]): TClass[] | null;
    function useFetchById(id: Id | Id[]): TClass | TClass[] | null {
        const dispatch = useAppDispatch();
        const list = useAppSelector(config.selector);

        const [entity, setEntity] = useState<TClass | TClass[] | null>(null);
        const timerRef = useRef<NodeJS.Timeout | null>(null);

        const normalizeIds = (ids: (string | null | undefined)[]): string[] =>
            ids.filter((el): el is string => typeof el === 'string' && el.trim().length > 0);

        const checkCache = (): boolean => {
            if (Array.isArray(id)) {
                const idsNorm = normalizeIds(id);
                if (idsNorm.length === 0) return false;

                const cached = idsNorm
                    .map(oneId => list.find(el => el.id === oneId))
                    .filter((x): x is TModel => Boolean(x));

                if (cached.length === idsNorm.length) {
                    setEntity(cached.map(config.mapToClass));
                    return true;
                }
            } else {
                if (!id || id.trim().length === 0) return false;

                const cached = list.find(el => el.id === id);
                if (cached) {
                    setEntity(config.mapToClass(cached));
                    return true;
                }
            }
            return false;
        };

        const processQueue = async () => {
            if (idQueueRef.size === 0) return;

            const queuedIds = Array.from(idQueueRef);
            idQueueRef.clear();

            const MAX_RETRIES = config.maxRetries ?? 2;

            // Map of resolved models (real from backend or fallback) keyed by ID
            const resolvedMap = new Map<string, TModel>();

            // IDs that are still pending resolution
            let pendingIds = queuedIds;

            for (let attempt = 0; attempt < MAX_RETRIES && pendingIds.length > 0; attempt++) {
                const fetched = await config.fetchFn(dispatch, pendingIds);

                const fetchedArray: TModel[] = fetched
                    ? Array.isArray(fetched)
                        ? fetched
                        : [fetched]
                    : [];

                if (fetchedArray.length === 0) {
                    // Backend returned nothing; no point in retrying in this attempt
                    break;
                }

                const fetchedIdsSet = new Set<string>();

                for (const item of fetchedArray) {
                    resolvedMap.set(item.id, item);
                    fetchedIdsSet.add(item.id);
                }

                // Filter IDs that are still unresolved
                pendingIds = pendingIds.filter(id => !fetchedIdsSet.has(id));
            }

            // For all IDs still unresolved after all retries, create minimal fallback models.
            if (pendingIds.length > 0) {
                for (const id of pendingIds) {
                    // Minimal model: only `id`. Your CEntity-based classes
                    // will treat missing fields as "Unknown".
                    const fallbackModel: TModel = { id } as TModel;
                    resolvedMap.set(id, fallbackModel);
                }
            }

            // Merge existing Redux list with the newly resolved items and deduplicate by ID
            const merged = [...list, ...Array.from(resolvedMap.values())];

            const dedupMap = new Map<string, TModel>();
            for (const item of merged) {
                dedupMap.set(item.id, item); // last one wins
            }

            const newValue = Array.from(dedupMap.values());

            dispatch(
                globalActions.updateGlobalState({
                    key: config.key,
                    value: newValue,
                })
            );
            // Important: we let `checkCache()` handle updating local state
            // in the effect when `list` changes.
        };

        const addToQueue = (oneId: string) => {
            const cleanId = oneId.trim();
            if (!cleanId) return;

            idQueueRef.add(cleanId);

            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                processQueue();
            }, 1000);
        };

        useEffect(() => {
            if (!id || (Array.isArray(id) && id.length === 0)) return;

            if (checkCache()) return;

            if (Array.isArray(id)) {
                normalizeIds(id).forEach(oneId => addToQueue(oneId));
            } else {
                addToQueue(id);
            }

            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            };
            // We stringify `id` to compare by value, and depend on `list`
            // so that when Redux updates, `checkCache()` can resolve the entities.
        }, [JSON.stringify(id), list]);

        return entity;
    }

    return useFetchById;
}
